import type { Family } from "@/features/training/queries";
import { cn } from "@/lib/cn";
import { BookOpen } from "lucide-react";
import { useState } from "react";

export interface RecipePick {
  id: string;
  name: string;
  version: number | null;
  training_config: Record<string, unknown>;
}

interface RecipeLike {
  id: string;
  name: string;
  family: Family | null;
  last_committed_version: number | null;
  training_config?: Record<string, unknown> | null;
}

interface Props {
  family: Family | null;
  recipes: RecipeLike[];
  selectedRecipeId: string | null;
  onPick: (pick: RecipePick | null) => void;
}

export function RecipeGuidancePicker({ family, recipes, selectedRecipeId, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const filtered = family ? recipes.filter((r) => r.family === family) : [];

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (!next) onPick(null);
        }}
        className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-navy-300"
      >
        <BookOpen className="size-3.5" />
        Use a recipe for guidance
      </button>
      {open && (
        <div className="mt-3">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted">No recipes for this family yet.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {filtered.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() =>
                      onPick({
                        id: r.id,
                        name: r.name,
                        version: r.last_committed_version,
                        training_config: r.training_config ?? {},
                      })
                    }
                    className={cn(
                      "flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-code-bg",
                      selectedRecipeId === r.id && "bg-accent/10 text-accent",
                    )}
                  >
                    <span className="font-medium">{r.name}</span>
                    <span className="font-mono text-[10px] text-muted">
                      v{r.last_committed_version ?? "—"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
